import fs from 'node:fs/promises';
import path from 'node:path';

type GitMetadata = {
  root: string;
  configPath: string;
};

async function pathExists(filePath: string): Promise<boolean> {
  try {
    await fs.stat(filePath);
    return true;
  } catch {
    return false;
  }
}

function resolveGitdir(gitFilePath: string, contents: string): string | null {
  const match = /^gitdir:\s*(.+)$/m.exec(contents.trim());
  const rawGitdir = match?.[1]?.trim();
  if (!rawGitdir) return null;
  return path.resolve(path.dirname(gitFilePath), rawGitdir);
}

async function configPathFromGitdir(gitdir: string): Promise<string | null> {
  const directConfigPath = path.join(gitdir, 'config');
  if (await pathExists(directConfigPath)) return directConfigPath;

  let commondir: string;
  try {
    commondir = (await fs.readFile(path.join(gitdir, 'commondir'), 'utf8')).trim();
  } catch {
    return null;
  }
  if (!commondir) return null;

  const commonConfigPath = path.join(path.resolve(gitdir, commondir), 'config');
  return (await pathExists(commonConfigPath)) ? commonConfigPath : null;
}

async function findGitMetadata(project: string): Promise<GitMetadata | null> {
  let dir = project.trim();
  if (!dir) return null;

  for (;;) {
    const gitPath = path.join(dir, '.git');
    try {
      const stat = await fs.stat(gitPath);
      if (stat.isDirectory()) {
        const configPath = path.join(gitPath, 'config');
        if (await pathExists(configPath)) return { root: dir, configPath };
      } else if (stat.isFile()) {
        const gitdir = resolveGitdir(gitPath, await fs.readFile(gitPath, 'utf8'));
        const configPath = gitdir ? await configPathFromGitdir(gitdir) : null;
        if (configPath) return { root: dir, configPath };
      }
    } catch {
      // Keep walking parents.
    }

    const parent = path.dirname(dir);
    if (parent === dir) return null;
    dir = parent;
  }
}

export async function findGitConfigPath(project: string): Promise<string | null> {
  const metadata = await findGitMetadata(project);
  return metadata?.configPath ?? null;
}

export async function findGitRoot(project: string): Promise<string | null> {
  const metadata = await findGitMetadata(project);
  return metadata?.root ?? null;
}

export async function repoOriginPathForProject(project: string): Promise<string> {
  const gitConfigPath = await findGitConfigPath(path.resolve(project.trim()));
  if (!gitConfigPath) return '';

  let gitConfig: string;
  try {
    gitConfig = await fs.readFile(gitConfigPath, 'utf8');
  } catch {
    return '';
  }

  const originURL = originRemoteURLFromGitConfig(gitConfig);
  const parsed = parseRepoOwnerName(originURL);
  return parsed ? `${parsed.owner}/${parsed.name}` : '';
}

export async function repoFilterForProject(project: string): Promise<string> {
  return (await repoOriginPathForProject(project)).toLowerCase();
}

export function originRemoteURLFromGitConfig(gitConfig: string): string {
  let inOriginRemote = false;
  for (const line of gitConfig.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith(';')) {
      continue;
    }
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      inOriginRemote = trimmed === '[remote "origin"]';
      continue;
    }
    if (!inOriginRemote) continue;

    const separator = trimmed.indexOf('=');
    if (separator < 0) continue;
    if (trimmed.slice(0, separator).trim() !== 'url') continue;
    return trimmed.slice(separator + 1).trim();
  }

  return '';
}

export function parseRepoOwnerName(
  remoteURL: string,
): { owner: string; name: string } | null {
  const trimmed = remoteURL.trim();
  if (!trimmed) return null;

  let pathPart = '';
  const sshMatch = /^(?:ssh:\/\/)?git@[^/:]+[:/](.+)$/.exec(trimmed);
  if (sshMatch) {
    pathPart = sshMatch[1] ?? '';
  } else {
    try {
      const url = new URL(trimmed);
      pathPart = url.pathname.replace(/^\/+/, '');
    } catch {
      pathPart = trimmed;
    }
  }

  pathPart = pathPart.replace(/\.git$/, '').replace(/^\/+|\/+$/g, '');
  const parts = pathPart.split('/').filter(Boolean);
  if (parts.length < 2) return null;

  const [owner, name] = parts.slice(-2);
  if (!owner || !name) return null;
  return { owner, name };
}
